import {db} from "../../firebase/config";
import {Bar, BarFromDB, BarSubmission, ChainSubmissionFromDB, NewBarSubmission, RawNewBar} from "../../interfaces/Bar";
import {
    collection,
    doc,
    getDoc,
    getDocs,
    query,
    runTransaction,
    Timestamp,
    Transaction,
    where
} from "firebase/firestore";
import {
    BarDeal,
    BarDealFromDB,
    BarDealSuggestion,
    BarDealSuggestionFromDB,
    ChainDeal,
    ChainDealFromDB,
    ChainDealSuggestion,
    ChainDealSuggestionFromDB,
    RawBarDeal,
    RawChainDeal
} from "../../interfaces/IDeal";
import {invalidateDataCache} from "../serverActions/InvalidateCache";
import {isEqual, sortBy} from "lodash";

export interface BarChanges {
    bar: BarSubmission,
    deals: BarDealSuggestion[],
    chainDeals: ChainDealSuggestion[],
    originalBar: Bar | undefined,
    originalDeals: BarDeal[],
    originalChainDeals: ChainDeal[] | undefined
}

export interface BarChangesForApproval extends Omit<BarChanges, "bar"|"deals"|"chainDeals">{
    bar: BarSubmission,
    deals: BarDealSuggestion[],
    chainDeals: ChainDealSuggestion[]
}

export interface NewBarChanges extends Omit<BarChanges, "bar"> {
    bar: NewBarSubmission,
}


export async function loadSubmissions(): Promise<BarChangesForApproval[]> {
    const submissions = (await getDocs(collection(db, "submissions_bars"))).docs.map(value => BarSubmission(value))
    return Promise.all(submissions.map(async barSubmission => {
        const originalBarDoc = await getDoc(doc(db, "bars/" + barSubmission.maps_id))
        const originalBar = await BarFromDB(originalBarDoc)
        const suggestedDeals = (await getDocs(query(
            collection(db, "submissions_deals"),
            where('bar', '==', doc(db, "submissions_bars", barSubmission.id))
        ))).docs.map(d => {
            return BarDealSuggestionFromDB(d)
        })

        const chain = barSubmission.chain?.id ? ChainSubmissionFromDB(await getDoc(doc(db, "submissions_chains", barSubmission.chain.id))) : undefined
        const suggestedChainDeals = barSubmission.chain?.id && chain ? (await getDocs(query(
            collection(db, "submissions_deals"),
            where('chain', '==', doc(db, "submissions_chains", barSubmission.chain.id))
        ))).docs.map(d => {
            return ChainDealSuggestionFromDB(d, chain)
        }) : []

        const originalChainDeals = barSubmission.chain && chain?.original ? (await getDocs(query(
            collection(db, "deals"),
            where('chain', '==', chain.original)
        ))).docs.map(data => ChainDealFromDB(data, chain)) : []

        if (originalBar !== undefined) {
            const originalDeals = (await getDocs(query(
                collection(db, "deals"),
                where('bar', '==', doc(db, "bars", originalBar.id))
            ))).docs.map(data => BarDealFromDB(data))

            return {
                bar: {
                    chain: chain,
                    id: barSubmission.id,
                    name: barSubmission.name,
                    address: barSubmission.address,
                    description: barSubmission.description,
                    maps_id: barSubmission.maps_id,
                    isClosed: barSubmission.isClosed,
                    location: barSubmission.location
                },
                deals: suggestedDeals,
                chainDeals: suggestedChainDeals,
                originalBar: originalBar,
                originalDeals: originalDeals,
                originalChainDeals: originalChainDeals,
            } as BarChangesForApproval
        } else {
            return {
                bar: barSubmission,
                deals: suggestedDeals,
                chainDeals: suggestedChainDeals,
                originalBar: undefined,
                originalDeals: [],
                originalChainDeals: originalChainDeals
            } as BarChangesForApproval
        }
    }))
}

export async function approveInTransaction(tx: Transaction, suggestion: BarChanges) {
    if (suggestion.originalBar) {
        // First delete old data.
        const originalBar = doc(db, "bars/" + suggestion.originalBar.id)
        tx.delete(originalBar) // Delete original bar
        // Delete original deals
        suggestion.originalDeals.forEach(originalDeal => {
            tx.delete(doc(db, "deals/" + originalDeal.id))
        })
        suggestion.originalChainDeals?.forEach(originalChainDeal => {
            tx.delete(doc(db, "deals/" + originalChainDeal.id))
        })
    }
    if (!suggestion.bar.isClosed) {
        const data = {
            name: suggestion.bar.name,
            description: suggestion.bar.description,
            address: suggestion.bar.address,
            chain: suggestion.bar.chain?.original ?? null,
            updated_at: Timestamp.now(),
            location: suggestion.bar.location
        } as RawNewBar
        const barDoc = doc(db, "bars/" + suggestion.bar.maps_id)
        tx.set(barDoc, data)
        if (suggestion.bar.chain?.original) {
            tx.update(suggestion.bar.chain.original, {
                description: suggestion.bar.chain.description
            })
        }
        suggestion.deals.forEach(deal => {
            const dealDoc = deal.original ?? doc(collection(db, "deals"))
            tx.set(dealDoc, {
                days: deal.days,
                description: deal.description,
                conditions: deal.conditions ?? "",
                bar: barDoc
            } as RawBarDeal)
        })
        suggestion.chainDeals.forEach(chainDeal => {
            const dealDoc = chainDeal.original ?? doc(collection(db, "deals"))
            tx.set(dealDoc, {
                days: chainDeal.days,
                description: chainDeal.description,
                conditions: chainDeal.conditions,
                chain: suggestion.bar.chain?.original ?? null
            } as RawChainDeal)
        })
        // If this bar is part of a chain and this submission updates some of the chain deals.
        function normalizeChainDealSuggestion(deals: ChainDealSuggestion[]): ChainDeal[] {
            return deals.map((deal) => {
                return {
                    chain: deal.chain,
                    conditions: deal.conditions,
                    id: deal.original?.id,
                    days: deal.days,
                    description: deal.description
                } as ChainDeal
            })
        }
        const suggestedChainDeals = sortBy(normalizeChainDealSuggestion(suggestion.chainDeals), ['id'])
        const originalChainDeals = sortBy(suggestion.originalChainDeals, ['id'])
        if (suggestion.bar.chain && (!suggestion.originalBar?.chain?.updated_at || !isEqual(suggestedChainDeals, originalChainDeals))) {
            // Update updated_at field for all bars in this chain.
            const chainRef = doc(db, "chains", suggestion.bar.chain.id)

            const q = query(collection(db, "bars"), where("chain", "==", chainRef))
            const otherBarIds = (await getDocs(q)).docs.map(value => value.id)
            otherBarIds.forEach(barId => {
                tx.update(doc(db, "bars", barId), {
                    updated_at: Timestamp.now()
                })
            })
            // Update updated_at on chain object.
            tx.update(chainRef, {
                updated_at: Timestamp.now()
            })
        }
    }
    await invalidateDataCache()
}

export async function approve(suggestion: BarChanges) {
    await runTransaction(db, async (tx) => {
        await approveInTransaction(tx, suggestion)
    })
}

export async function deleteSubmission(suggestion: BarChanges) {
    console.log("Deleting suggestion, ", suggestion.bar)
    await runTransaction(db, async (tx) => {
        const dealsCollection = collection(db, "submissions_deals")
        suggestion.deals.forEach(deal => {
            tx.delete(doc(dealsCollection, deal.id))
        })
        suggestion.chainDeals.forEach(deal => {
            tx.delete(doc(dealsCollection, deal.id))
        })
        tx.delete(doc(collection(db, "submissions_bars"), suggestion.bar.id))
        if (suggestion.bar.chain) tx.delete(doc(db, "submissions_chains", suggestion.bar.chain.id))
    })
}